import { cn } from '@/lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { SlashSquare } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import type {
  ProtocolsMenuProps,
  ShortcutSearchViewModel,
} from './protocols-button.types';
import {
  ShortcutMenuBrowseAll,
  ShortcutMenuList,
  type ShortcutMenuItemProps,
} from './shortcut-menu-list';
import { ShortcutSaveToggle } from './shortcut-save-toggle';

export type ProtocolsDesktopDropdownProps = ProtocolsMenuProps;

const ITEM_CLASS = 'group cursor-pointer';

const ShortcutDropdownItem = ({
  accessoryEnd,
  children,
  ...rest
}: ShortcutMenuItemProps) => (
  <DropdownMenuItem {...rest}>
    {children}
    {accessoryEnd}
  </DropdownMenuItem>
);

/**
 * Flat "/" search results: every matching shortcut with an inline save toggle.
 * The highlighted row (keyboard + Enter target) is styled and scrolled into
 * view as the user navigates.
 */
const SearchResults = ({
  search,
  isAtShortcutLimit,
}: {
  search: ShortcutSearchViewModel;
  isAtShortcutLimit: boolean;
}) => {
  const intl = useIntl();
  const { results, highlightedIndex, onSelect, onSave, onUnsave } = search;
  const highlightedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  if (results.length === 0) {
    return (
      <div
        className="px-2 py-6 text-center text-sm text-text-tertiary"
        data-testid="evidence-shortcuts-search-empty"
      >
        {intl.formatMessage({
          defaultMessage: 'No matching shortcuts',
          id: '9OLFVAQe5h',
          description:
            'Empty state when the floating Ask Heidi "/" shortcut search has no matches',
        })}
      </div>
    );
  }

  return (
    <>
      {results.map((shortcut, index) => {
        const isHighlighted = index === highlightedIndex;
        return (
          <DropdownMenuItem
            key={shortcut.id}
            ref={isHighlighted ? highlightedRef : undefined}
            onClick={() => onSelect(shortcut)}
            className={cn(ITEM_CLASS, isHighlighted && 'bg-surface-hover')}
            data-testid="evidence-shortcuts-search-item"
            data-shortcut-label={shortcut.label}
          >
            <SlashSquare size={14} className="text-text-secondary" />
            <span className="truncate">{shortcut.label}</span>
            <ShortcutSaveToggle
              isSaved={shortcut.isSaved}
              disabled={!shortcut.isSaved && isAtShortcutLimit}
              onToggle={() =>
                shortcut.isSaved ? onUnsave?.(shortcut) : onSave(shortcut)
              }
            />
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

export const ProtocolsDesktopDropdown = ({
  isOpen,
  onOpenChange,
  triggerButton,
  tooltipText,
  pinnedChips,
  onPinnedChipClick,
  onBrowseAllClick,
  activeCuratedProtocols,
  onProtocolSelect,
  recommendedShortcuts = [],
  onRecommendedSelect,
  onSaveShortcut,
  onUnsaveShortcut,
  isAtShortcutLimit = false,
  search,
  onRetainInputFocus,
}: ProtocolsDesktopDropdownProps) => {
  const isSearching = Boolean(search?.isActive);

  // Guided "/" search (onRetainInputFocus set): type into TipTap, not the menu.
  // Flag-off / legacy keeps Radix modal + default focus behaviour.
  const retainInputFocus = Boolean(onRetainInputFocus);

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={onOpenChange}
      modal={retainInputFocus ? false : true}
    >
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        </TooltipTrigger>
        {!isOpen && (
          <TooltipContent side="top" sideOffset={8}>
            {tooltipText}
          </TooltipContent>
        )}
      </Tooltip>
      <DropdownMenuContent
        align="start"
        side="top"
        sideOffset={8}
        className="flex max-h-[26rem] w-64 flex-col p-0"
        data-testid="evidence-shortcuts-menu"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onFocusCapture={
          retainInputFocus
            ? (e) => {
                // Radix remounts menuitems on each query change and focuses them —
                // Backspace then only works once. Bounce caret to TipTap; allow
                // real <button>s (save / browse).
                const target = e.target as HTMLElement;
                if (target.closest('button')) return;
                onRetainInputFocus?.();
              }
            : undefined
        }
        onFocusOutside={
          retainInputFocus
            ? (e) => {
                // Guided focus lives in the composer (focused on open,
                // and the bounce above returns it), so focus leaving the menu
                // is not a dismiss, as in the modal menu. A non-modal Radix
                // menu closes on focus-outside: without this, opening from the
                // trigger or hovering a row closes it. Escape, outside press
                // and selection still close it.
                e.preventDefault();
              }
            : undefined
        }
        {...(retainInputFocus
          ? ({
              // Private on Radix Menu Content types; honored at runtime.
              onOpenAutoFocus: (event: Event) => {
                event.preventDefault();
              },
            } as Record<string, (event: Event) => void>)
          : {})}
      >
        {/* Scrollable body: the list scrolls; the footer below stays pinned. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {isSearching && search ? (
            <SearchResults search={search} isAtShortcutLimit={isAtShortcutLimit} />
          ) : (
            <ShortcutMenuList
              Item={ShortcutDropdownItem}
              pinnedChips={pinnedChips}
              onPinnedChipClick={onPinnedChipClick}
              onUnsaveShortcut={onUnsaveShortcut}
              recommendedShortcuts={recommendedShortcuts}
              onRecommendedSelect={onRecommendedSelect}
              onSaveShortcut={onSaveShortcut}
              isAtShortcutLimit={isAtShortcutLimit}
              activeCuratedProtocols={activeCuratedProtocols}
              onProtocolSelect={onProtocolSelect}
            />
          )}
        </div>

        {/* Sticky footer: always visible regardless of list length. */}
        <div className="border-t border-border p-1">
          <ShortcutMenuBrowseAll
            Item={ShortcutDropdownItem}
            onBrowse={onBrowseAllClick}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
